/**
 * Le categorie come **albero**, che è quello che sono.
 *
 * ## Perché un albero, e perché non c'era
 *
 * Le categorie di questa piattaforma hanno un padre (`categories."parentId"`, che l'operatore
 * trascina nel pannello) e profondità libera: «Film › Azione › Spionaggio › Guerra fredda». Al
 * player arrivavano piatte, perché il livello compatibile scriveva `parent_id: 0` su ogni riga — un
 * campo che non taceva, **diceva il falso**. Ottanta categorie tutte sorelle in una fila sola: tre
 * quarti del catalogo irraggiungibili, e nessun modo di accorgersene leggendo la risposta.
 *
 * ## Le due regole che decidono tutto
 *
 * **Un titolo del figlio conta anche per il padre.** Chi apre «Azione» si aspetta di trovarci anche
 * le spy story: un conteggio che esclude i discendenti farebbe sembrare vuoti proprio i nodi che
 * raggruppano di più. Quindi `count` è il numero di titoli del nodo **e di tutta la sua discendenza**.
 *
 * **Un padre che non esiste è una radice.** Può succedere per davvero: `get_vod_categories`
 * restituisce le categorie che hanno titoli, e un antenato senza titoli propri ci arriva solo perché
 * il backend lo aggiunge apposta. Se per qualsiasi motivo manca, il figlio non deve sparire — si
 * appende alla radice, che è una posizione discutibile ma visibile. Sparire no.
 *
 * ## Un anello non deve spegnere l'app
 *
 * L'albero lo costruisce un dato che arriva dalla rete, e `a` padre di `b` con `b` padre di `a` è
 * una cosa che il database non vieta: percorrerlo senza difese è uno stack overflow, cioè
 * un'applicazione che si chiude da sola. La difesa è un insieme dei nodi già visti, e ogni
 * attraversamento qui sotto lo consulta — `buildCategoryTree` mentre scende, `idsUnder` con una pila
 * esplicita. La profondità vera è tre o quattro livelli, quindi la ricorsione non è il problema:
 * lo è il ciclo.
 */

import { compareCatalogNames } from './catalogSort.js';

/** Un nodo dell'albero: la categoria, i suoi figli, e quanti titoli porta in tutto. */
export interface CategoryNode {
  readonly id: string;
  readonly name: string;
  readonly children: ReadonlyArray<CategoryNode>;
  /** I titoli del nodo **e di tutta la sua discendenza**: vedi la regola in testa al modulo. */
  readonly count: number;
  /** I titoli che stanno esattamente qui, senza i discendenti. Serve alla diagnosi, non al disegno. */
  readonly ownCount: number;
}

interface FlatCategory {
  readonly id: string;
  readonly name: string;
  readonly parentId: string | null;
}

/**
 * L'albero delle categorie, con i conteggi.
 *
 * `countsById` sono i titoli che dichiarano **quella** categoria. Le categorie senza nome non
 * arrivano fin qui — `parseCategories` le scarta — ma quelle senza titoli sì, ed è giusto: un nodo
 * che raggruppa e basta deve comparire, altrimenti la catena si spezza.
 *
 * L'ordine dei figli è quello in cui le categorie sono arrivate, cioè `categories.position`: è
 * l'unica dichiarazione d'ordine che l'operatore abbia fatto, e riordinare qui per nome la
 * cancellerebbe.
 */
export function buildCategoryTree(
  categories: ReadonlyArray<FlatCategory>,
  countsById: ReadonlyMap<string, number>,
): ReadonlyArray<CategoryNode> {
  const known = new Set(categories.map((category) => category.id));

  const childrenOf = new Map<string, FlatCategory[]>();
  const roots: FlatCategory[] = [];

  for (const category of categories) {
    /*
      Un padre che non sta nell'elenco non è un padre: il figlio diventa una radice invece di
      sparire. Vedi la regola in testa al modulo.

      Vale anche per il padre che è **sé stessi**: è il ciclo più corto che esista, e senza questa
      riga quella categoria sarebbe figlia di sé e non comparirebbe da nessuna parte.
    */
    const parent =
      category.parentId !== null && category.parentId !== category.id && known.has(category.parentId)
        ? category.parentId
        : null;
    if (parent === null) {
      roots.push(category);
      continue;
    }
    const siblings = childrenOf.get(parent);
    if (siblings === undefined) childrenOf.set(parent, [category]);
    else siblings.push(category);
  }

  const seen = new Set<string>();

  const build = (category: FlatCategory): CategoryNode => {
    /*
      L'insieme dei visti è la rete contro gli anelli.

      `a` padre di `b` e `b` padre di `a` è un dato che il database non vieta e che una risposta
      malformata può contenere: senza questa riga il ramo si percorre all'infinito.
    */
    seen.add(category.id);
    const children: CategoryNode[] = [];
    for (const child of childrenOf.get(category.id) ?? []) {
      if (seen.has(child.id)) continue;
      children.push(build(child));
    }
    const ownCount = countsById.get(category.id) ?? 0;
    const count = children.reduce((total, child) => total + child.count, ownCount);
    return { id: category.id, name: category.name, children, count, ownCount };
  };

  const tree = roots.filter((root) => !seen.has(root.id)).map(build);

  /*
    **Chi è rimasto fuori diventa una radice**, e questa riga esiste per un caso solo: l'anello.

    `a` padre di `b` e `b` padre di `a` non producono **nessuna** radice — ognuno dei due ha un padre
    che l'elenco contiene — quindi senza questa passata l'albero uscirebbe vuoto e quelle categorie
    sparirebbero insieme a tutti i loro titoli. Non è un caso teorico: basta un import che scrive i
    padri in due passaggi.

    Spezzare l'anello nel punto in cui si incontra è arbitrario, e va bene che lo sia: qualunque
    punto è arbitrario, e l'alternativa è far sparire del catalogo senza dirlo a nessuno.
  */
  for (const category of categories) {
    if (seen.has(category.id)) continue;
    tree.push(build(category));
  }

  return tree;
}

/**
 * Il nodo in fondo a un percorso di identificativi, o `null` se il percorso non esiste più.
 *
 * `null` e non «il più vicino che c'è»: un percorso che non si risolve significa che il catalogo è
 * cambiato sotto i piedi — una categoria tolta, un import rifatto — e portare chi guarda in un posto
 * che non ha chiesto è peggio che riportarlo indietro.
 */
export function nodeAt(
  roots: ReadonlyArray<CategoryNode>,
  path: ReadonlyArray<string>,
): CategoryNode | null {
  let level = roots;
  let found: CategoryNode | null = null;
  for (const id of path) {
    found = level.find((node) => node.id === id) ?? null;
    if (found === null) return null;
    level = found.children;
  }
  return found;
}

/**
 * I nodi attraversati da un percorso, dalla radice al nodo corrente: è il percorso in cima alla
 * pagina.
 *
 * Vuoto quando il percorso non si risolve, per la stessa ragione di `nodeAt`.
 */
export function trailAt(
  roots: ReadonlyArray<CategoryNode>,
  path: ReadonlyArray<string>,
): ReadonlyArray<CategoryNode> {
  const trail: CategoryNode[] = [];
  let level = roots;
  for (const id of path) {
    const found = level.find((node) => node.id === id);
    if (found === undefined) return [];
    trail.push(found);
    level = found.children;
  }
  return trail;
}

/**
 * Tutti gli identificativi di un nodo e della sua discendenza.
 *
 * È quel che serve per dire «questo titolo appartiene al nodo aperto»: un titolo in «Spionaggio»
 * deve comparire anche aprendo «Azione», altrimenti i nodi che raggruppano sembrano vuoti.
 */
export function idsUnder(node: CategoryNode): ReadonlySet<string> {
  const ids = new Set<string>();
  const stack: CategoryNode[] = [node];
  while (stack.length > 0) {
    const current = stack.pop() as CategoryNode;
    if (ids.has(current.id)) continue;
    ids.add(current.id);
    for (const child of current.children) stack.push(child);
  }
  return ids;
}

/**
 * Le radici in ordine alfabetico, per la schermata «Tutti i generi».
 *
 * Alfabetico **qui e non nell'albero**: l'elenco completo si legge cercando un nome, e per cercare
 * un nome serve l'alfabeto. Nelle pastiglie della vetrina vale invece l'ordine dell'operatore, che è
 * una scelta editoriale — sono due domande diverse poste allo stesso dato.
 *
 * Passa da `compareCatalogNames`, che ignora emoji e punteggiatura iniziale: i nomi veri di questo
 * catalogo cominciano quasi tutti per emoji, e ordinare quelle ordina le emoji.
 */
export function rootsAlphabetically(roots: ReadonlyArray<CategoryNode>): ReadonlyArray<CategoryNode> {
  return [...roots].sort((a, b) => compareCatalogNames(a.name, b.name));
}
